/**
 * Android hardware back-button handling for the Capacitor build.
 *
 * The default Capacitor behaviour is to exit the app (or walk WebView history)
 * on a hardware back press. On a chart-first app that's a trap: a student deep
 * in a menu taps back to close it and the whole app quits instead. So we take
 * the press over and use it to unwind whatever is open, one step at a time, only
 * letting it fall through to the OS (exit) when nothing is open.
 *
 * It's a small priority stack rather than a single handler because the thing
 * that should close first (a confirm dialog, say) lives in a different component
 * from the thing that should close last (the menu's drill level). Each interested
 * component registers a handler with a priority; on a back press we call them
 * from highest priority down and stop at the first one that says it consumed the
 * press. Nothing consumes it -> we let the OS do its default (exit).
 *
 * Nothing here runs on the web build: install is a no-op off a native platform,
 * and the plugin is only imported dynamically inside install so a browser bundle
 * never has to resolve it.
 */
import { Capacitor } from '@capacitor/core';

/** A back handler returns true if it consumed the press (stop here). */
export type BackHandler = () => boolean;

interface Entry {
  fn: BackHandler;
  priority: number;
}

const handlers: Entry[] = [];

/**
 * Register a back handler. Higher priority runs first. Returns an unregister
 * function (call it on unmount) so stale handlers never linger.
 */
export function pushBackHandler(fn: BackHandler, priority = 0): () => void {
  const entry: Entry = { fn, priority };
  handlers.push(entry);
  return () => {
    const i = handlers.indexOf(entry);
    if (i >= 0) handlers.splice(i, 1);
  };
}

/**
 * Run the registered handlers, highest priority first, and stop at the first
 * that consumes the press. Returns true if something handled it. Exported for
 * unit testing without a native runtime.
 */
export function handleBack(): boolean {
  // Copy + sort so a handler unregistering mid-run can't corrupt iteration.
  const ordered = [...handlers].sort((a, b) => b.priority - a.priority);
  for (const h of ordered) {
    try {
      if (h.fn()) return true;
    } catch {
      // A throwing handler shouldn't swallow the press for the ones below it.
    }
  }
  return false;
}

let installed = false;

/**
 * Wire the Capacitor App backButton to our stack. Safe to call more than once
 * (only the first call binds) and a no-op on the web build.
 */
export async function installAndroidBackButton(): Promise<void> {
  if (installed || !Capacitor.isNativePlatform()) return;
  installed = true;
  const { App } = await import('@capacitor/app');
  await App.addListener('backButton', () => {
    // If nothing we know about is open, fall back to the OS default: exit the
    // app (there's no in-app WebView history to walk on a single-page chart).
    if (!handleBack()) App.exitApp();
  });
}
